// filepath: apps/web/src/i18n/format.ts
//
// FR-I18N-2 — Locale-Aware Dates, Numbers, and Currencies.
//
// Single shared helper module that wraps the browser-native `Intl.*`
// family for the three shipped locales (`ru` / `kk` / `en` — see
// `apps/web/src/i18n/useTranslation.ts::Locale`).
//
// Per ARCHITECTURE_WEB.md §8 ("Dates, numbers, currencies: `Intl` API
// — locale from session, timezone from session or browser.") and
// ARCHITECTURE_v0.4.md §12 item 13 ("Dates, numbers, and currencies
// render per user locale/region. Designed in now rather than
// retrofitted.").
//
// All four helpers are pure pass-throughs to `Intl.*` with locale
// awareness. They never throw `RangeError` for an invalid `Date` —
// `formatDate` returns the `"—"` placeholder so the renderer layout
// does not collapse. `formatCurrency` does NOT swallow an invalid
// ISO 4217 currency code — that is a developer error and must
// surface to the caller (consistent with FR-UI-CORE-7 sanitization
// behavior; a misconfigured currency code in production is a
// shippable bug that tests must catch).
//
// DEFAULT STYLES
//   - `formatDate` defaults `dateStyle` and `timeStyle` to
//     `"medium"` / `"medium"` — the same defaults the existing
//     `SummaryCard.formatComputedAt` helper uses (the seeded
//     precedent).
//   - `formatNumber` defaults `style` to `"decimal"`.
//   - `formatPercent` is a thin shim that delegates to
//     `formatNumber` with `style: "percent"` — the `Intl.*` API
//     already does the right thing for percent values; one-symbol
//     convenience.
//
// TIMEZONE CONTRACT (AC-6)
//   When `timeZone` is omitted, `formatDate` uses the browser's
//   default IANA zone (default `Intl.DateTimeFormat` behavior).
//   The per-call `timeZone` override is supported but not required
//   by any current surface; no tenant-default or session-stored
//   timezone source is invented in v1.
//
// INTEGRATION
//   - The four helpers are re-exported from `apps/web/src/i18n/index.ts`.
//   - The `SummaryCard.formatComputedAt` / inline `Intl.NumberFormat`
//     call sites are migrated to use `formatDate` / `formatNumber`
//     (preserving the existing public contract — `formatComputedAt`
//     becomes a thin wrapper).
//   - The two pre-existing no-arg `.toLocaleString()` call sites
//     (`SecretReferencesTable.tsx`, `TemplateRegistryTable.tsx`)
//     are migrated to use `formatDate` with the active locale from
//     `useTranslation()`.
//
// SECURITY
//   - All four helpers are pure functions of the input value +
//     locale + options. No I/O. No DOM access. No eval / Function
//     construction. They cannot inject HTML because they call
//     `Intl.*` only.
//   - INV-9 (no `dangerouslySetInnerHTML`) is upheld: the return
//     values are plain strings intended for React text-node
//     rendering, never for raw HTML interpolation.

import type { Locale } from "./useTranslation";

/** Placeholder returned by `formatDate` for an invalid `Date`. */
const INVALID_DATE_PLACEHOLDER = "—";

/**
 * Options for `formatDate`. Mirrors the subset of
 * `Intl.DateTimeFormatOptions` that the v1 surfaces use; we keep
 * the surface narrow so the helper cannot accidentally widen into
 * a "kitchen-sink" date API.
 */
export interface FormatDateOptions {
  dateStyle?: "full" | "long" | "medium" | "short";
  timeStyle?: "full" | "long" | "medium" | "short";
  /** IANA timezone override. Defaults to the browser's IANA zone. */
  timeZone?: string;
}

/**
 * Format a `Date` value for the given locale. Returns `"—"` for
 * an invalid `Date` (or non-`Date` input) so the renderer's layout
 * does not collapse.
 *
 * Defaults `dateStyle` / `timeStyle` to `"medium"` / `"medium"` —
 * matches the `SummaryCard.formatComputedAt` precedent (the
 * seeded caller).
 *
 * Pure: does not throw. The `Intl.DateTimeFormat` constructor
 * itself throws `RangeError` for invalid locale tags, but our
 * `Locale` type is constrained to `"ru" | "kk" | "en"` so the
 * constructor cannot encounter an unknown tag at runtime — TypeScript
 * blocks it at the call site.
 */
export function formatDate(
  value: Date,
  locale: Locale,
  options: FormatDateOptions = {},
): string {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return INVALID_DATE_PLACEHOLDER;
  }
  const intlOptions: Intl.DateTimeFormatOptions = {
    dateStyle: options.dateStyle ?? "medium",
    timeStyle: options.timeStyle ?? "medium",
  };
  if (options.timeZone !== undefined) {
    intlOptions.timeZone = options.timeZone;
  }
  return new Intl.DateTimeFormat(locale, intlOptions).format(value);
}

/**
 * Options for `formatNumber`. Mirrors the subset of
 * `Intl.NumberFormatOptions` that v1 surfaces use; the surface is
 * kept narrow on purpose so the helper cannot accidentally widen
 * into a "kitchen-sink" number API.
 */
export interface FormatNumberOptions {
  style?: "decimal" | "percent" | "currency";
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
}

/**
 * Format a numeric value for the given locale. Defaults `style`
 * to `"decimal"`. Pass `style: "percent"` for percent rendering
 * (or use `formatPercent`).
 *
 * Pure: does not throw.
 */
export function formatNumber(
  value: number,
  locale: Locale,
  options: FormatNumberOptions = {},
): string {
  const intlOptions: Intl.NumberFormatOptions = {
    style: options.style ?? "decimal",
  };
  if (options.minimumFractionDigits !== undefined) {
    intlOptions.minimumFractionDigits = options.minimumFractionDigits;
  }
  if (options.maximumFractionDigits !== undefined) {
    intlOptions.maximumFractionDigits = options.maximumFractionDigits;
  }
  if (options.useGrouping !== undefined) {
    intlOptions.useGrouping = options.useGrouping;
  }
  return new Intl.NumberFormat(locale, intlOptions).format(value);
}

/**
 * Format a numeric value as a locale-aware percent.
 * Thin shim around `formatNumber` with `style: "percent"`.
 * Existing callers that hand-build percent strings
 * (e.g. `Math.round(x * 100) + "%"`) should migrate to this helper.
 */
export function formatPercent(
  value: number,
  locale: Locale,
  options: Omit<FormatNumberOptions, "style"> = {},
): string {
  return formatNumber(value, locale, { ...options, style: "percent" });
}

/**
 * Format a numeric amount as a currency value. `currencyCode` is
 * an ISO 4217 three-letter code (e.g. `"RUB"`, `"USD"`, `"EUR"`).
 *
 * Pure: does not throw `RangeError` for an invalid currency code —
 * if `currencyCode` is not a valid ISO 4217 tag, `Intl.NumberFormat`
 * will throw `RangeError` synchronously and the helper does NOT
 * swallow it. This is intentional: an invalid currency code is a
 * developer error, not a runtime locale-state error, and must
 * surface to the test (consistent with FR-UI-CORE-7 sanitization
 * — a misconfigured currency code in production is a shippable
 * bug that tests must catch).
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  locale: Locale,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
}

/**
 * Format a `Date` value with a literal pattern (locale-INDEPENDENT).
 *
 * ISS-442 (counterparties UX): the BA spec mandates `dd-MM-yyyy
 * HH:mm` for the operator-visible list + detail date columns.
 * The pattern is literal (lowercase `yyyy` for calendar year, NOT
 * uppercase `YYYY` which is "week-year" — a different semantic
 * that shifts the year near January 1; see
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#:~:text=year%20and%20weekYear)
 * and https://www.unicode.org/reports/tr35/tr35-dates.html#dfst-weekyear).
 *
 * `Intl.DateTimeFormat` only supports the `dateStyle` / `timeStyle`
 * shortcuts (and named fields like `year`, `month`, etc.) — it
 * does NOT support arbitrary patterns. So we build the formatted
 * parts via `formatToParts` and assemble the literal pattern
 * ourselves. The browser's default IANA timezone is used (no
 * `timeZone` override) per the BA spec ("the user's browser
 * timezone").
 *
 * Returns `"—"` for an invalid `Date` (or non-`Date` input) —
 * matches the `formatDate` placeholder contract.
 *
 * Pure: does not throw. The `Intl.DateTimeFormat` constructor
 * itself throws `RangeError` for invalid locale tags, but our
 * `Locale` type is constrained at the call site.
 *
 * SECURITY / INV-9: the return value is a plain string rendered
 * through React text nodes; no `dangerouslySetInnerHTML` path.
 */
export function formatDatePattern(
  value: Date,
  locale: Locale,
  pattern: "dd-MM-yyyy HH:mm" = "dd-MM-yyyy HH:mm",
): string {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return INVALID_DATE_PLACEHOLDER;
  }
  // Build the parts via Intl.DateTimeFormat.formatToParts. The
  // locale-independent literal pattern is what the BA spec
  // requires (the digits are not localized — "15-10-2026 14:30"
  // is identical in ru / kk / en).
  const dtf = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(value);
  // The parts array carries: day, month, year, hour, minute (and
  // possibly literal separators). We pull the numeric values
  // directly so the literal pattern wins over any locale's
  // preferred digit shape (some locales use Arabic-Indic digits
  // for the 2-digit field; the BA spec wants ASCII 0-9).
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  // Pad day / month / hour / minute to 2 digits with a leading 0
  // (the "2-digit" option does this in most locales but some
  // locales use native digits — we want ASCII for the BA spec).
  const pad2 = (s: string): string => s.padStart(2, "0");
  if (pattern === "dd-MM-yyyy HH:mm") {
    return `${pad2(day)}-${pad2(month)}-${year} ${pad2(hour)}:${pad2(minute)}`;
  }
  // Defensive: the only pattern supported right now is the BA
  // spec one. Future patterns should be added as explicit cases
  // above (and unit-tested in format.test.ts).
  return `${pad2(day)}-${pad2(month)}-${year} ${pad2(hour)}:${pad2(minute)}`;
}
